if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
        this.onSubmitHandler = this.onSubmitHandler.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton && this.submitButton.querySelector('span');
        this.defaultSubmitLabel =
          (this.submitButtonText && this.submitButtonText.textContent.trim()) ||
          (this.submitButton && this.submitButton.getAttribute('aria-label')) ||
          '';

        this.form.removeEventListener('submit', this.onSubmitHandler);
        this.form.addEventListener('submit', this.onSubmitHandler);
        window.PITBQuantityLimit?.syncAddForms();
      }

      getSpinner() {
        return this.querySelector('.loading__spinner');
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('loading', Boolean(isLoading));

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.setAttribute('aria-busy', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('aria-busy');
        }

        if (this.submitButtonText) {
          this.submitButtonText.textContent = isLoading
            ? 'Adding...'
            : this.defaultSubmitLabel || 'Add to cart';
        } else if (this.submitButton.hasAttribute('aria-label')) {
          this.submitButton.setAttribute(
            'aria-label',
            isLoading ? 'Adding to cart' : this.defaultSubmitLabel
          );
        }

        // Loading UI is the icon/button style — never show spinner.
        const spinner = this.getSpinner();
        if (spinner) spinner.classList.add('hidden');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.form || !this.submitButton) return;
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();
        this.setLoading(true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }
        config.body = formData;

        const request = window.PITBQuantityLimit
          ? window.PITBQuantityLimit.addToCart(`${routes.cart_add_url}`, config)
          : fetch(`${routes.cart_add_url}`, config);
        request
          .then((response) => response ? response.json() : null)
          .then((response) => {
            if (!response) return;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message || 'Could not add to cart');
              this.error = true;

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', 'true');
                if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: response }));
            this.error = false;

            if (this.cart && typeof this.cart.renderContents === 'function') {
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.handleErrorMessage('Could not add to cart. Please try again.');
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Always restore button unless sold-out state is showing permanently.
            const soldOutVisible =
              this.error &&
              this.submitButton.querySelector('.sold-out-message:not(.hidden)');
            if (soldOutVisible) {
              this.submitButton.classList.remove('loading');
              const spinner = this.getSpinner();
              if (spinner) spinner.classList.add('hidden');
              return;
            }

            this.setLoading(false);
            window.PITBQuantityLimit?.syncAddForms();
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) {
          if (errorMessage) console.warn('[product-form]', errorMessage);
          return;
        }
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form && this.form.querySelector('[name=id]');
      }
    }
  );
}
